// frontend/src/pages/canvasser/DoorPage.tsx
import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Flex, HStack, Spinner, Text, VStack } from '@chakra-ui/react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toaster } from '@/components/ui/toaster'
import { useMyTurfs, useResolveScript, useStopsDoneHere } from '../../hooks/useCanvasser'
import { useGrantCampaign } from '../../hooks/useGrantCampaign'
import { useSync } from '../../providers/SyncProvider'
import { doorToast, useRecordDoor } from '../../hooks/useRecordDoor'
import { serverMessage } from '../../lib/syncEngine'
import { buildWalkStops, stopSignature } from '../../lib/walkStops'
import {
  readScriptLanguage, readWalkPrefs, rememberLeftStop, writeScriptLanguage, writeWalkPrefs, type ScriptLanguage,
} from '../../lib/walkPrefs'
import { clearPath, loadPath, walkConversation } from '../../lib/conversation'
import {
  allowedDoorFlags, allowedDoorResults, withAllowedFlags, clearDraft, conversationKey, flipRequests, handledIds, loadDraft, newDraft, outcomes, peopleForDoorMiss,
  saveDraft, setEntry, toVisitRequests,
  type CouldntKnockReason, type DoorDraft, type DraftEntry, type DraftOutcome, type EndChoice, type FlipHome,
  type FollowUps, type OffListInfo, type ScriptAnswers,
} from '../../lib/doorDraft'
import { RESULT_STATUS_LABELS, ResultStatus, VisitActionFlag } from '../../types/script'
import type { TurfContactDto } from '../../types/canvasser'
import { DoorHeader } from '../../components/canvasser/door/DoorHeader'
import { ConversationStep } from '../../components/canvasser/door/ConversationStep'
import { InfoSheet } from '../../components/canvasser/door/info/InfoSheet'
import { NobodyHomeSheet } from '../../components/canvasser/door/NobodyHomeSheet'
import { CouldntKnockSheet } from '../../components/canvasser/door/CouldntKnockSheet'
import { WhoAnsweredStep, type PersonAction } from '../../components/canvasser/door/WhoAnsweredStep'
import { EndStep } from '../../components/canvasser/door/EndStep'
import { RollUpStep, type RollUpChoice } from '../../components/canvasser/door/RollUpStep'
import { BeforeYouGoInStep } from '../../components/canvasser/door/BeforeYouGoInStep'
import { NotAtWorkSheet } from '../../components/canvasser/door/NotAtWorkSheet'
import { DoorScreenStep, type DoorAction } from '../../components/canvasser/door/DoorScreenStep'
import { OffListSheet, type OffListPerson } from '../../components/canvasser/door/OffListSheet'

type Step =
  | { kind: 'arrival' } | { kind: 'door' } | { kind: 'who' } | { kind: 'rollup' }
  | { kind: 'conversation'; ids: string[] }
  | { kind: 'end'; ids: string[]; preset: EndChoice | null; answers: ScriptAnswers | null; notice: string | null; fromConversation: boolean }
type Sheet = null | 'nobodyHome' | 'couldntKnock' | 'offList' | 'leave' | 'leaveConversation' | { notAtWork: string }

const NO_KEYS: ReadonlySet<string> = new Set()
const NO_SCRIPT = 'No script for this door. Choose how it ended.'
const nameOf = (c: { firstName: string; lastName?: string | null }) => `${c.firstName} ${c.lastName ?? ''}`.trim()

function entryLabel(e: DraftEntry): string {
  if (e.outcome.flags.includes(VisitActionFlag.NotAtThisAddress)) return "Doesn't live here"
  if (e.outcome.flags.includes(VisitActionFlag.NotAtThisWorksite)) return "Doesn't work here"
  if (e.outcome.flags.includes(VisitActionFlag.NotAtWork)) return 'Not at work today'
  // [Spec §17.2] "Stephanie: Talked ✓": a finished conversation reads as talked, not "Completed".
  if (e.outcome.result === ResultStatus.Completed) return 'Talked'
  return RESULT_STATUS_LABELS[e.outcome.result] ?? e.outcome.result
}

/**
 * [Spec §4.4-§4.9, §15.1 D1-D5, Phase 4; home stops per §17.2] One door, start to finish. Keyed per door so no state (draft,
 * step, a sheet's reason or a selection) ever carries from one door to the next.
 */
export function DoorPage() {
  const { stopKey = '' } = useParams()
  const [params] = useSearchParams()
  const turfId = params.get('turfId') ?? ''
  return <Door key={`${turfId}|${stopKey}`} turfId={turfId} stopKey={stopKey} focusId={params.get('contactId')} />
}

/**
 * All state lives here; the step components are presentational. Everything recorded lands in the door
 * draft (persisted per door, ids minted once) and goes to the server as ONE batch on finish.
 */
function Door({ turfId, stopKey, focusId }: { turfId: string; stopKey: string; focusId: string | null }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const campaign = useGrantCampaign()
  const { isOnline } = useSync()
  const { data: turfs, isLoading } = useMyTurfs(campaign?.id)
  const turf = turfs?.find(t => t.turfId === turfId)
  const doneHere = useStopsDoneHere(turfId || undefined)
  const stop = useMemo(
    () => (turf ? buildWalkStops(turf.contacts, turf.addressMode, doneHere.data ?? NO_KEYS).find(s => s.key === stopKey) ?? null : null),
    [turf, doneHere.data, stopKey])
  // [§17.2] The dock's person (contact walk / Contacts view) only orders the door's list; nobody is pre-selected.
  const focus = stop?.residents.find(r => r.contactId === focusId) ?? null
  const doorScript = useResolveScript(campaign?.id, (focus ?? stop?.residents[0])?.contactId, turfId || undefined)
  const allowed = useMemo(() => allowedDoorResults(doorScript.data), [doorScript.data])
  const allowedFlags = useMemo(() => allowedDoorFlags(doorScript.data), [doorScript.data])

  const [draft, setDraft] = useState<DoorDraft>(() => loadDraft(turfId, stopKey) ?? newDraft(turfId, stopKey))
  // [R16] Once the door is sent (or discarded) and its draft cleared, nothing may write it back.
  const doneRef = useRef(false)
  useEffect(() => { if (!doneRef.current) saveDraft(draft) }, [draft])
  const [added, setAdded] = useState<OffListPerson[]>([])
  const [step, setStep] = useState<Step | null>(null)
  const [sheet, setSheet] = useState<Sheet>(null)
  const [infoOpen, setInfoOpen] = useState(false)
  const convIds = step?.kind === 'conversation' ? step.ids : null
  // The conversation's script (resolved for its first person, P4-4). Same query key as before, so cached.
  const convScript = useResolveScript(campaign?.id, convIds?.[0], convIds ? turfId || undefined : undefined)
  const [language, setLanguage] = useState<ScriptLanguage>(readScriptLanguage)
  const [leftLiterature, setLeftLiterature] = useState(() => readWalkPrefs().leftLiterature)
  const [saving, setSaving] = useState(false)
  const savingRef = useRef(false)
  const recordDoor = useRecordDoor().mutateAsync

  useEffect(() => {
    if (step || !stop) return
    if (stop.kind === 'worksite') setStep(draft.entries.length > 0 ? { kind: 'who' } : { kind: 'arrival' })
    else setStep({ kind: 'door' })
  }, [step, stop, draft.entries.length])

  if (isLoading || (!turfs && !stop)) return <VStack py={16}><Spinner size="lg" color="accent" /></VStack>
  if (!stop || !campaign) {
    return (
      <VStack py={16} px={6} gap={4}>
        <Text textAlign="center">This door isn't on your list any more.</Text>
        <Button minH="48px" onClick={() => navigate('/canvasser')}>Back to my walk</Button>
      </VStack>
    )
  }

  // Everyone this door can record: the stop's residents plus anyone added here as "not on my list".
  const people: TurfContactDto[] = [
    ...(focus ? [focus, ...stop.residents.filter(r => r !== focus)] : stop.residents),
    ...added.filter(a => !stop.residents.some(r => r.contactId === a.contactId))
      .map(a => ({ contactId: a.contactId, firstName: a.name, lastName: '' }) as TurfContactDto),
  ]
  const names = new Map(people.map(p => [p.contactId, nameOf(p)]))
  const offListOf = (id: string): OffListInfo | null => added.find(a => a.contactId === id)?.offList ?? null
  const handled = handledIds(draft)
  const handledLabels = new Map(draft.entries.map(e => [e.contactId, entryLabel(e)]))
  const unhandled = stop.residents.filter(r => !handled.has(r.contactId))
  const isWorksite = stop.kind === 'worksite'

  const put = (d: DoorDraft, ids: string[], outcome: DraftOutcome,
    extras: { answers?: ScriptAnswers | null; flipHome?: FlipHome | null } = {}) =>
    ids.reduce((acc, id) => setEntry(acc, { contactId: id, name: names.get(id) ?? '', outcome: withAllowedFlags(outcome, allowedFlags),
      answers: extras.answers ?? null, offList: offListOf(id), flipHome: extras.flipHome ?? null }), d)

  async function finish(d: DoorDraft) {
    if (savingRef.current || !stop || !campaign) return
    savingRef.current = true
    setSaving(true)
    // [R16] Keep (and persist) the exact draft being sent BEFORE sending: a retry after a throw, or a
    // reload, re-sends the same doorVisitId and clientVisitIds instead of minting new ones.
    setDraft(d)
    saveDraft(d)
    let result
    try {
      result = await recordDoor({ visits: toVisitRequests(d, campaign.id), flips: flipRequests(d),
        doneHere: isWorksite ? { turfId, stopKey } : null })
    } catch (err) {
      toaster.create({ type: 'error', title: serverMessage(err, "Couldn't save this door. Your work is kept here; try again.") })
      savingRef.current = false
      setSaving(false)
      return
    }
    doneRef.current = true
    toaster.create(doorToast(result))
    clearDraft(turfId, stopKey)
    rememberLeftStop(turfId, { key: stopKey, state: stop.state, sig: stopSignature(stop), recorded: true })
    navigate('/canvasser')
  }

  const discard = () => {
    doneRef.current = true
    clearDraft(turfId, stopKey)
    navigate('/canvasser')
  }
  const leave = () => {
    if (draft.entries.length > 0) { setSheet('leave'); return }
    discard()
  }
  const rememberLeftLit = (v: boolean) => { setLeftLiterature(v); writeWalkPrefs({ ...readWalkPrefs(), leftLiterature: v }) }
  const convKey = (ids: string[]) => conversationKey(turfId, stopKey, ids)
  const backFromConversation = (): Step => (isWorksite ? { kind: 'who' } : { kind: 'door' })
  const toEnd = (ids: string[], preset: EndChoice | null, answers: ScriptAnswers | null) =>
    setStep({ kind: 'end', ids, preset, answers, notice: null, fromConversation: true })
  // [P5-2] × in a conversation leaves the conversation, not the door; with answers it asks first.
  const onHeaderClose = () => {
    // × on How did it end? reached from a conversation = "Back to the conversation", never leave the door.
    if (step?.kind === 'end' && step.fromConversation) { setStep({ kind: 'conversation', ids: step.ids }); return }
    if (step?.kind !== 'conversation') { leave(); return }
    if (loadPath(convKey(step.ids)).length > 0) setSheet('leaveConversation')
    else setStep(backFromConversation())
  }
  const answersSoFar = (ids: string[]): ScriptAnswers | null => {
    const data = convScript.data
    return data ? { scriptId: data.scriptId, ...walkConversation(data.elements, loadPath(convKey(ids))).results } : null
  }
  const toggleLanguage = () => {
    const next: ScriptLanguage = language === 'en' ? 'es' : 'en'
    setLanguage(next)
    writeScriptLanguage(next)
  }
  // [P4-2] A worksite miss records the first open employee only; a home door, everyone (§17.2: contact walks too).
  const missPeople = (): TurfContactDto[] =>
    isWorksite ? peopleForDoorMiss(stop.residents).slice(0, 1) : peopleForDoorMiss(stop.residents)
  const afterHomeWork = (d: DoorDraft) => {
    const left = stop.residents.filter(r => !handledIds(d).has(r.contactId))
    if (left.length > 0) setStep({ kind: 'rollup' })
    else void finish(d)
  }

  const onPersonAction = (id: string, action: PersonAction) => {
    if (action === 'notAtWork') { setSheet({ notAtWork: id }); return }
    const outcome = { moved: outcomes.moved, deceased: outcomes.deceased, doesntLiveHere: outcomes.doesntLiveHere,
      doesntWorkHere: outcomes.doesntWorkHere }[action]()
    setDraft(put(draft, [id], outcome))
  }
  // [§17.2] Refused / Language barrier go to How did it end?; the rest record straight onto the door screen.
  const onDoorAction = (id: string, action: DoorAction) => {
    if (action === 'refused' || action === 'language') {
      setStep({ kind: 'end', ids: [id], preset: action === 'refused' ? 'declined' : 'language', answers: null, notice: null, fromConversation: false })
      return
    }
    onPersonAction(id, action)
  }
  const onEndConfirm = (ids: string[], answers: ScriptAnswers | null) => (choice: EndChoice, f: FollowUps) => {
    if (f.leftLiterature !== leftLiterature) rememberLeftLit(f.leftLiterature)
    const d = put(draft, ids, outcomes.fromEnd(choice, f), { answers })
    clearPath(convKey(ids))
    setDraft(d)
    if (isWorksite) setStep({ kind: 'who' })
    // [§17.2] Back to the door screen; once every listed resident has a disposition, the door is done.
    else if (stop.residents.every(r => handledIds(d).has(r.contactId))) void finish(d)
    else setStep({ kind: 'door' })
  }
  const onRollUp = (choices: Record<string, RollUpChoice>) => {
    const by: Record<RollUpChoice, () => DraftOutcome> = { notHome: () => outcomes.notHome(false), moved: outcomes.moved,
      deceased: outcomes.deceased, refused: outcomes.refused }
    void finish(Object.entries(choices).reduce((acc, [id, ch]) => put(acc, [id], by[ch]()), draft))
  }
  const onAdded = (p: OffListPerson) => {
    setAdded(a => [...a.filter(x => x.contactId !== p.contactId), p])
    setSheet(null)
    qc.invalidateQueries({ queryKey: ['canvasser', 'turfs'] })
    setStep({ kind: 'conversation', ids: [p.contactId] })
  }
  const notAtWorkId = sheet && typeof sheet === 'object' ? sheet.notAtWork : null
  const notAtWorkPerson = notAtWorkId ? people.find(p => p.contactId === notAtWorkId) ?? null : null

  const talkingTo = step?.kind === 'conversation' || step?.kind === 'end' ? step.ids.map(id => names.get(id) ?? '').join(', ') : null
  const place = isWorksite ? stop.workplaceLabel ?? stop.address : stop.address
  const title = talkingTo ?? place
  const subtitle = talkingTo ? stop.address
    : isWorksite ? stop.address : `${people.length} ${people.length === 1 ? 'person' : 'people'}`
  const infoFor = step?.kind === 'conversation' || step?.kind === 'end' ? step.ids[0] : focus?.contactId ?? null

  return (
    <Flex direction="column" h="100%" minH={0}>
      <DoorHeader title={title} subtitle={subtitle} onClose={onHeaderClose} onInfo={() => setInfoOpen(true)} menu={[]}
        language={step?.kind === 'conversation' ? { value: language, onToggle: toggleLanguage } : undefined} />

      {sheet === 'leave' && (
        <Box m={4} p={3} borderWidth="1px" borderColor="border" borderRadius="md" bg="bg.subtle">
          <Text mb={2}>{`Leave this door? ${draft.entries.length} recorded here ${draft.entries.length === 1 ? "isn't" : "aren't"} saved yet.`}</Text>
          <HStack gap={2} wrap="wrap">
            <Button minH="44px" bg="accent" color="accent.fg" disabled={saving} onClick={() => void finish(draft)}>Save and leave</Button>
            <Button minH="44px" variant="outline" borderColor="border" disabled={saving} onClick={discard}>Discard</Button>
            <Button minH="44px" variant="ghost" onClick={() => setSheet(null)}>Stay</Button>
          </HStack>
        </Box>
      )}
      {sheet === 'leaveConversation' && step?.kind === 'conversation' && (
        <Box m={4} p={3} borderWidth="1px" borderColor="border" borderRadius="md" bg="bg.subtle">
          <Text mb={2}>Stop this conversation?</Text>
          <HStack gap={2} wrap="wrap">
            <Button minH="44px" bg="accent" color="accent.fg" onClick={() => { setSheet(null); toEnd(step.ids, null, answersSoFar(step.ids)) }}>End conversation</Button>
            <Button minH="44px" variant="outline" borderColor="border"
              onClick={() => { clearPath(convKey(step.ids)); setSheet(null); setStep(backFromConversation()) }}>Discard answers</Button>
            <Button minH="44px" variant="ghost" onClick={() => setSheet(null)}>Keep talking</Button>
          </HStack>
        </Box>
      )}

      <Flex direction="column" flex={1} minH={0} pt={3}>
        {step?.kind === 'arrival' && (
          <BeforeYouGoInStep workplaceLabel={stop.workplaceLabel} address={stop.address} arrival={stop.arrival}
            worksiteInfo={stop.worksiteInfo} isSaving={saving}
            canCouldntGetIn={allowed.has(ResultStatus.Inaccessible)}
            onInside={() => setStep({ kind: 'who' })} onNobodyAvailable={() => void finish(draft)}
            onCouldntGetIn={() => setSheet('couldntKnock')} />
        )}
        {step?.kind === 'door' && (
          <DoorScreenStep people={people} recorded={handledLabels} allowed={allowed} isSaving={saving}
            onTalk={id => setStep({ kind: 'conversation', ids: [id] })} onAction={onDoorAction}
            onNotOnList={() => setSheet('offList')} onNobodyAnswered={() => setSheet('nobodyHome')}
            onCouldntKnock={() => setSheet('couldntKnock')} onFinish={() => afterHomeWork(draft)} />
        )}
        {step?.kind === 'who' && (
          <WhoAnsweredStep key={`${turfId}|${stopKey}`} variant="worksite" residents={people}
            handled={handledLabels} allowed={allowed} isSaving={saving}
            onStart={ids => setStep({ kind: 'conversation', ids })}
            onRefused={ids => setStep({ kind: 'end', ids, preset: 'declined', answers: null, notice: null, fromConversation: false })}
            onLanguageBarrier={ids => setStep({ kind: 'end', ids, preset: 'language', answers: null, notice: null, fromConversation: false })}
            onPersonAction={onPersonAction} onNotOnList={() => setSheet('offList')}
            finishLabel="Done here" onFinish={() => void finish(draft)} />
        )}
        {step?.kind === 'conversation' && (
          <ConversationStep script={convScript.data} isLoading={convScript.isLoading} isError={convScript.isError}
            stateKey={convKey(step.ids)} language={language}
            onFinish={answers => toEnd(step.ids, 'finished', answers)}
            onEndEarly={answers => toEnd(step.ids, null, answers)}
            onNoScript={() => setStep({ kind: 'end', ids: step.ids, preset: 'finished', answers: null, notice: NO_SCRIPT, fromConversation: false })} />
        )}
        {step?.kind === 'end' && (
          <EndStep key={`${step.ids.join(',')}|${step.preset ?? 'none'}`} names={step.ids.map(id => names.get(id) ?? '').join(', ')}
            allowed={allowed} allowedFlags={allowedFlags} preset={step.preset} notice={step.notice} rememberedLeftLiterature={leftLiterature} isSaving={saving}
            onBack={step.fromConversation ? () => setStep({ kind: 'conversation', ids: step.ids }) : undefined}
            onConfirm={onEndConfirm(step.ids, step.answers)} />
        )}
        {step?.kind === 'rollup' && (
          <RollUpStep people={unhandled} allowed={allowed} isSaving={saving} onDone={onRollUp}
            onTalkToSomeoneElse={() => setStep({ kind: 'door' })} />
        )}
      </Flex>

      <NobodyHomeSheet open={sheet === 'nobodyHome'} title="Nobody home"
        names={missPeople().map(nameOf)} leftLiterature={leftLiterature} onLeftLiteratureChange={rememberLeftLit}
        isSaving={saving} onClose={() => setSheet(null)}
        offerLeftLiterature={allowedFlags.has(VisitActionFlag.LeftLiterature)} comeBackLater={allowedFlags.has(VisitActionFlag.ComeBackLater)}
        onRecord={() => void finish(put(draft, missPeople().map(p => p.contactId), outcomes.notHome(leftLiterature)))} />
      <CouldntKnockSheet key={`${turfId}|${stopKey}`} open={sheet === 'couldntKnock'}
        title={isWorksite ? "Couldn't get in" : "Couldn't knock"} isSaving={saving} onClose={() => setSheet(null)}
        onRecord={(reason: CouldntKnockReason, other: string) =>
          void finish(put(draft, missPeople().map(p => p.contactId), outcomes.couldntKnock(reason, other, isWorksite)))} />
      {notAtWorkPerson && (
        <NotAtWorkSheet open name={notAtWorkPerson.firstName} onClose={() => setSheet(null)}
          canVisitHome={turf?.addressMode === 'Hybrid' && notAtWorkPerson.homeLatitude != null && notAtWorkPerson.homeLongitude != null
            && !notAtWorkPerson.opportunisticStopId}
          onConfirm={visitHome => {
            setDraft(put(draft, [notAtWorkPerson.contactId], outcomes.notAtWork(), {
              flipHome: visitHome ? { turfMemberId: notAtWorkPerson.turfMemberId, movedFromLabel: stop.workplaceLabel ?? 'work' } : null,
            }))
            setSheet(null)
          }} />
      )}
      {sheet === 'offList' && stop.residents[0] && (
        <OffListSheet open onClose={() => setSheet(null)} variant={isWorksite ? 'worksite' : 'home'}
          turfId={turfId} anchorTurfMemberId={stop.residents[0].turfMemberId} unionTier={turf?.unionTier ?? false}
          isOnline={isOnline} onAdded={onAdded}
          onTheyMovedOut={() => { setSheet(null); void finish(put(draft, unhandled.map(r => r.contactId), outcomes.moved())) }} />
      )}
      {/* [R17] Mounted only while open, so it always opens on the person in focus. */}
      {infoOpen && <InfoSheet onClose={() => setInfoOpen(false)} people={people} initialContactId={infoFor} />}
    </Flex>
  )
}
